import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputBase,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
  createTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import SwapVertOutlinedIcon from '@mui/icons-material/SwapVertOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import { getApiUrl } from '../utils/api';
import { apiFetch } from '../utils/apiFetch';
import { useAuth } from '../auth/AuthContext';
import PageMenuButton from '../components/PageMenuButton';
import { canManageInputs } from '../utils/auth';

const API_URL = getApiUrl();

const theme = createTheme({
  palette: {
    primary: { main: '#6366f1', light: '#818cf8', dark: '#4f46e5' },
    secondary: { main: '#f59e0b', light: '#fbbf24', dark: '#d97706' },
    background: { default: '#f8fafc', paper: '#ffffff' },
    success: { main: '#10b981' },
    warning: { main: '#f59e0b' },
    error: { main: '#ef4444' },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      background: 'linear-gradient(135deg, #6366f1 0%, #f59e0b 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 8, textTransform: 'none', fontWeight: 600 },
        contained: {
          background: 'linear-gradient(135deg, #6366f1 0%, #f59e0b 100%)',
          '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #d97706 100%)' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 8, fontWeight: 600 },
      },
    },
  },
});

const emptyInput = {
  name: '',
  categoryId: '',
  purchaseUnit: 'Kg',
  quantityPerPackage: '',
  currentStockBase: '',
  minimumStockBase: '',
  purchaseCost: '',
  active: true,
};

const MOVEMENT_LABELS = {
  entrada: 'Entrada manual',
  saida: 'Saída manual',
  ajuste: 'Ajuste de inventário',
  perda: 'Perda',
  venda: 'Venda',
  estorno: 'Estorno',
};

const formatCurrency = (value) => Number(value || 0).toLocaleString('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const formatNumber = (value) => Number(value || 0).toLocaleString('pt-BR', {
  maximumFractionDigits: 3,
});

const toNumber = (value) => {
  const numeric = Number(String(value ?? '').replace(',', '.'));
  return Number.isFinite(numeric) ? numeric : 0;
};

const resolveConversion = (purchaseUnit, quantityPerPackage) => {
  if (purchaseUnit === 'L') return { baseUnit: 'ml', conversionFactor: 1000 };
  if (purchaseUnit === 'Cx') return { baseUnit: 'un', conversionFactor: toNumber(quantityPerPackage) };
  return { baseUnit: 'gr', conversionFactor: 1000 };
};

const fetchJson = async (url, options = {}) => {
  const response = await apiFetch(url, options);
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    throw new Error(data.error || 'Falha ao processar solicitação.');
  }
  return data;
};

const normalizeInputForm = (input = null) => ({
  name: input?.name || '',
  categoryId: input?.categoryId || '',
  purchaseUnit: input?.purchaseUnit || 'Kg',
  quantityPerPackage: input?.quantityPerPackage || '',
  currentStockBase: input?.currentStockBase || '',
  minimumStockBase: input?.minimumStockBase || '',
  purchaseCost: input?.purchaseCost || '',
  active: input?.active !== false,
});

const Estoque = () => {
  const auth = useAuth();
  const canManage = canManageInputs(auth.user);
  const [inputs, setInputs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [filters, setFilters] = useState({
    search: '',
    categoryId: 'all',
    purchaseUnit: 'all',
    active: 'active',
    lowStock: false,
  });
  const [inputDialog, setInputDialog] = useState({ open: false, input: null });
  const [inputForm, setInputForm] = useState(emptyInput);
  const [inputDialogError, setInputDialogError] = useState('');
  const [categoryDialog, setCategoryDialog] = useState({ open: false });
  const [categoryDialogError, setCategoryDialogError] = useState('');
  const [categoryDrafts, setCategoryDrafts] = useState({});
  const [newCategoryName, setNewCategoryName] = useState('');
  const [quickCategoryName, setQuickCategoryName] = useState('');
  const [movementDialog, setMovementDialog] = useState({ open: false, input: null });
  const [movementDialogError, setMovementDialogError] = useState('');
  const [movementForm, setMovementForm] = useState({ movementType: 'entrada', quantityBase: '', purchaseQuantity: '', targetStockBase: '', reason: '' });
  const [historyDialog, setHistoryDialog] = useState({ open: false, input: null });
  const [movements, setMovements] = useState([]);
  const [loadingMovements, setLoadingMovements] = useState(false);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [cmvDialogOpen, setCmvDialogOpen] = useState(false);
  const [cmvReport, setCmvReport] = useState({ products: [], orders: [] });
  const [loadingCmv, setLoadingCmv] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [inputData, categoryData, issueData] = await Promise.all([
        fetchJson(`${API_URL}/api/inputs`),
        fetchJson(`${API_URL}/api/inputs/categories`),
        fetchJson(`${API_URL}/api/inputs/issues`),
      ]);
      setInputs(inputData.inputs || []);
      setCategories(categoryData.categories || []);
      setIssues(issueData.issues || []);
      setCategoryDrafts((categoryData.categories || []).reduce((acc, category) => ({ ...acc, [category.id]: category.name }), {}));
    } catch (err) {
      setError(err.message || 'Falha ao carregar estoque.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredInputs = useMemo(() => {
    const query = filters.search.trim().toLowerCase();
    return inputs.filter((input) => {
      const matchesSearch = !query ||
        String(input.name || '').toLowerCase().includes(query) ||
        String(input.categoryName || '').toLowerCase().includes(query);
      const matchesCategory = filters.categoryId === 'all' || String(input.categoryId || '') === String(filters.categoryId);
      const matchesUnit = filters.purchaseUnit === 'all' || input.purchaseUnit === filters.purchaseUnit;
      const matchesActive =
        filters.active === 'all' ||
        (filters.active === 'active' && input.active !== false) ||
        (filters.active === 'inactive' && input.active === false);
      const isLowStock = Number(input.minimumStockBase || 0) > 0 && Number(input.currentStockBase || 0) <= Number(input.minimumStockBase || 0);
      const matchesLowStock = !filters.lowStock || isLowStock;
      return matchesSearch && matchesCategory && matchesUnit && matchesActive && matchesLowStock;
    });
  }, [filters, inputs]);

  const stats = useMemo(() => {
    const activeInputs = inputs.filter((input) => input.active !== false);
    const lowStock = activeInputs.filter((input) => Number(input.minimumStockBase || 0) > 0 && Number(input.currentStockBase || 0) <= Number(input.minimumStockBase || 0));
    const inventoryValue = activeInputs.reduce((sum, input) => sum + Number(input.currentStockBase || 0) * Number(input.baseUnitCost || 0), 0);
    return {
      active: activeInputs.length,
      lowStock: lowStock.length,
      categories: categories.filter((category) => category.active !== false).length,
      inventoryValue,
    };
  }, [categories, inputs]);

  const openInputDialog = (input = null) => {
    setError('');
    setInputDialogError('');
    setQuickCategoryName('');
    setInputForm(normalizeInputForm(input));
    setInputDialog({ open: true, input });
  };

  const conversion = resolveConversion(inputForm.purchaseUnit, inputForm.quantityPerPackage);
  const baseUnitCost = conversion.conversionFactor > 0 ? toNumber(inputForm.purchaseCost) / conversion.conversionFactor : 0;

  const saveInput = async () => {
    if (!canManage) return;
    setSaving(true);
    setInputDialogError('');
    try {
      const editing = Boolean(inputDialog.input?.id);
      const payload = {
        ...inputForm,
        categoryId: inputForm.categoryId || null,
        quantityPerPackage: inputForm.purchaseUnit === 'Cx' ? toNumber(inputForm.quantityPerPackage) : null,
        currentStockBase: editing ? undefined : toNumber(inputForm.currentStockBase),
        minimumStockBase: toNumber(inputForm.minimumStockBase),
        purchaseCost: toNumber(inputForm.purchaseCost),
      };
      await fetchJson(`${API_URL}/api/inputs${editing ? `/${inputDialog.input.id}` : ''}`, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setInputDialog({ open: false, input: null });
      setToast({ open: true, severity: 'success', message: editing ? 'Item de estoque atualizado.' : 'Item de estoque criado.' });
      await loadData();
    } catch (err) {
      setInputDialogError(err.message || 'Falha ao salvar item de estoque.');
    } finally {
      setSaving(false);
    }
  };

  const deleteInput = async (input) => {
    if (!canManage) return;
    if (!window.confirm(`Excluir ou inativar o item de estoque "${input.name}"?`)) return;
    setSaving(true);
    try {
      const data = await fetchJson(`${API_URL}/api/inputs/${input.id}`, { method: 'DELETE' });
      setToast({
        open: true,
        severity: 'success',
        message: data.mode === 'inactivated' ? 'Item de estoque inativado por possuir vínculos.' : 'Item de estoque excluído.',
      });
      await loadData();
    } catch (err) {
      setToast({ open: true, severity: 'error', message: err.message || 'Falha ao excluir item de estoque.' });
    } finally {
      setSaving(false);
    }
  };

  const createQuickCategory = async () => {
    if (!canManage) return;
    const name = quickCategoryName.trim();
    if (!name) return;
    setSaving(true);
    setInputDialogError('');
    try {
      const data = await fetchJson(`${API_URL}/api/inputs/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const category = data.category;
      setCategories((prev) => {
        const next = prev.filter((item) => item.id !== category.id);
        return [...next, category].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
      });
      setCategoryDrafts((prev) => ({ ...prev, [category.id]: category.name }));
      setInputForm((prev) => ({ ...prev, categoryId: category.id }));
      setQuickCategoryName('');
      setToast({ open: true, severity: 'success', message: 'Categoria criada e selecionada.' });
    } catch (err) {
      setInputDialogError(err.message || 'Falha ao criar categoria.');
    } finally {
      setSaving(false);
    }
  };

  const saveCategory = async (category = null) => {
    if (!canManage) return;
    setSaving(true);
    setCategoryDialogError('');
    try {
      const editing = Boolean(category?.id);
      await fetchJson(`${API_URL}/api/inputs/categories${editing ? `/${category.id}` : ''}`, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editing ? categoryDrafts[category.id] : newCategoryName,
          active: category?.active !== false,
        }),
      });
      setNewCategoryName('');
      setToast({ open: true, severity: 'success', message: editing ? 'Categoria atualizada.' : 'Categoria criada.' });
      await loadData();
    } catch (err) {
      setCategoryDialogError(err.message || 'Falha ao salvar categoria.');
    } finally {
      setSaving(false);
    }
  };

  const deleteCategory = async (category) => {
    if (!canManage) return;
    if (!window.confirm(`Excluir ou inativar a categoria "${category.name}"?`)) return;
    setSaving(true);
    try {
      const data = await fetchJson(`${API_URL}/api/inputs/categories/${category.id}`, { method: 'DELETE' });
      setToast({
        open: true,
        severity: 'success',
        message: data.mode === 'inactivated' ? 'Categoria inativada por possuir itens de estoque.' : 'Categoria excluída.',
      });
      await loadData();
    } catch (err) {
      setToast({ open: true, severity: 'error', message: err.message || 'Falha ao excluir categoria.' });
    } finally {
      setSaving(false);
    }
  };

  const openMovementDialog = (input) => {
    setMovementDialogError('');
    setMovementForm({ movementType: 'entrada', quantityBase: '', purchaseQuantity: '', targetStockBase: input.currentStockBase || '', reason: '' });
    setMovementDialog({ open: true, input });
  };

  const saveMovement = async () => {
    if (!canManage) return;
    const input = movementDialog.input;
    if (!input) return;
    setSaving(true);
    setMovementDialogError('');
    try {
      const inputConversion = Number(input.conversionFactor || 0);
      const entradaQuantityBase = inputConversion > 0 ? toNumber(movementForm.purchaseQuantity) * inputConversion : 0;
      const payload = {
        movementType: movementForm.movementType,
        quantityBase: movementForm.movementType === 'entrada' ? entradaQuantityBase : toNumber(movementForm.quantityBase),
        targetStockBase: toNumber(movementForm.targetStockBase),
        reason: movementForm.reason,
      };
      await fetchJson(`${API_URL}/api/inputs/${input.id}/movements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setMovementDialog({ open: false, input: null });
      setToast({ open: true, severity: 'success', message: 'Movimentação registrada.' });
      await loadData();
    } catch (err) {
      setMovementDialogError(err.message || 'Falha ao registrar movimentação.');
    } finally {
      setSaving(false);
    }
  };

  const resolveIssue = async (issue) => {
    if (!canManage) return;
    setSaving(true);
    try {
      await fetchJson(`${API_URL}/api/inputs/issues/${issue.id}/resolve`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolutionNote: 'Resolvido manualmente na página de estoque.' }),
      });
      setToast({ open: true, severity: 'success', message: 'Alerta resolvido.' });
      await loadData();
    } catch (err) {
      setToast({ open: true, severity: 'error', message: err.message || 'Falha ao resolver alerta.' });
    } finally {
      setSaving(false);
    }
  };

  const reprocessIssue = async (issue) => {
    if (!canManage) return;
    setSaving(true);
    try {
      const data = await fetchJson(`${API_URL}/api/inputs/issues/${issue.id}/reprocess`, { method: 'POST' });
      setToast({
        open: true,
        severity: data.resolved ? 'success' : 'warning',
        message: data.resolved ? 'Baixa reprocessada e alerta resolvido.' : 'Reprocessamento concluído, mas o alerta ainda precisa de revisão.',
      });
      await loadData();
    } catch (err) {
      setToast({ open: true, severity: 'error', message: err.message || 'Falha ao reprocessar alerta.' });
    } finally {
      setSaving(false);
    }
  };

  const openCmvDialog = async () => {
    setCmvDialogOpen(true);
    setLoadingCmv(true);
    try {
      const data = await fetchJson(`${API_URL}/api/inputs/reports/cmv`);
      setCmvReport(data.report || { products: [], orders: [] });
    } catch (err) {
      setToast({ open: true, severity: 'error', message: err.message || 'Falha ao carregar CMV.' });
    } finally {
      setLoadingCmv(false);
    }
  };

  const openHistoryDialog = async (input) => {
    setHistoryDialog({ open: true, input });
    setMovements([]);
    setLoadingMovements(true);
    try {
      const data = await fetchJson(`${API_URL}/api/inputs/${input.id}/movements`);
      setMovements(data.movements || []);
    } catch (err) {
      setToast({ open: true, severity: 'error', message: err.message || 'Falha ao carregar histórico.' });
    } finally {
      setLoadingMovements(false);
    }
  };

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: { xs: 1.5, md: 2 }, background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', minHeight: '100vh' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 1.5 }}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <PageMenuButton />
              <Typography variant="h4">Estoque</Typography>
            </Stack>
            <Typography color="text.secondary">Ficha técnica, estoque base e movimentações</Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button variant="outlined" startIcon={<AssessmentOutlinedIcon />} onClick={openCmvDialog}>
              CMV
            </Button>
            {canManage ? (
              <>
                <Button variant="outlined" startIcon={<CategoryOutlinedIcon />} onClick={() => { setCategoryDialogError(''); setCategoryDialog({ open: true }); }}>
                  Categorias
                </Button>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => openInputDialog()}>
                  Adicionar item
                </Button>
              </>
            ) : null}
          </Stack>
        </Stack>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {issues.filter((issue) => !issue.resolved).length ? (
          <Alert
            severity="warning"
            icon={<WarningAmberOutlinedIcon />}
            sx={{ mb: 2 }}
            action={<Button color="inherit" size="small" onClick={() => setIssueDialogOpen(true)}>Revisar</Button>}
          >
            {issues.filter((issue) => !issue.resolved).length} alerta(s) de baixa automática pendente(s) de revisão.
          </Alert>
        ) : null}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">Ativos</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{stats.active}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">Estoque baixo</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: stats.lowStock ? 'warning.main' : 'success.main' }}>{stats.lowStock}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">Categorias</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{stats.categories}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">Valor estimado</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{formatCurrency(stats.inventoryValue)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: '#f1f5f9', borderRadius: 2, px: 2, py: 1, border: '1px solid #e2e8f0' }}>
                <SearchIcon sx={{ color: 'primary.main', mr: 1 }} />
                <InputBase
                  fullWidth
                  placeholder="Buscar item..."
                  value={filters.search}
                  onChange={(event) => setFilter('search', event.target.value)}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Categoria</InputLabel>
                <Select label="Categoria" value={filters.categoryId} onChange={(event) => setFilter('categoryId', event.target.value)}>
                  <MenuItem value="all">Todas</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Unidade</InputLabel>
                <Select label="Unidade" value={filters.purchaseUnit} onChange={(event) => setFilter('purchaseUnit', event.target.value)}>
                  <MenuItem value="all">Todas</MenuItem>
                  <MenuItem value="Kg">Kg</MenuItem>
                  <MenuItem value="L">L</MenuItem>
                  <MenuItem value="Cx">Cx</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={filters.active} onChange={(event) => setFilter('active', event.target.value)}>
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="active">Ativos</MenuItem>
                  <MenuItem value="inactive">Inativos</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControlLabel
                control={<Switch checked={filters.lowStock} onChange={(event) => setFilter('lowStock', event.target.checked)} />}
                label="Estoque baixo"
              />
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredInputs.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Inventory2OutlinedIcon sx={{ fontSize: 44, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">Nenhum item encontrado</Typography>
          </Paper>
        ) : (
          <Stack spacing={1.5}>
            {filteredInputs.map((input) => {
              const lowStock = Number(input.minimumStockBase || 0) > 0 && Number(input.currentStockBase || 0) <= Number(input.minimumStockBase || 0);
              return (
                <Paper key={input.id} sx={{ p: 2, borderRadius: 3, border: lowStock ? '1px solid #f59e0b' : '1px solid #e2e8f0' }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, md: 3 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {lowStock ? <WarningAmberOutlinedIcon color="warning" /> : <Inventory2OutlinedIcon color="primary" />}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="h6" sx={{ fontWeight: 800 }} noWrap>{input.name}</Typography>
                          <Typography variant="body2" color="text.secondary">{input.categoryName || 'Sem categoria'}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">Atual</Typography>
                      <Typography sx={{ fontWeight: 800 }}>{formatNumber(input.currentStockBase)} {input.baseUnit}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">Mínimo</Typography>
                      <Typography sx={{ fontWeight: 800 }}>{formatNumber(input.minimumStockBase)} {input.baseUnit}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">Compra</Typography>
                      <Typography sx={{ fontWeight: 800 }}>{input.purchaseUnit}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">Custo base</Typography>
                      <Typography sx={{ fontWeight: 800 }}>{formatCurrency(input.baseUnitCost)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4, md: 1 }}>
                      <Chip size="small" color={input.active ? 'success' : 'default'} label={input.active ? 'Ativo' : 'Inativo'} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
                      <Stack direction="row" spacing={0.8} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                        {canManage ? (
                          <Tooltip title="Movimentar estoque">
                            <IconButton color="primary" onClick={() => openMovementDialog(input)}>
                              <SwapVertOutlinedIcon />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        <Tooltip title="Histórico">
                          <IconButton onClick={() => openHistoryDialog(input)}>
                            <HistoryOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                        {canManage ? (
                          <>
                            <Tooltip title="Editar">
                              <IconButton color="primary" onClick={() => openInputDialog(input)}>
                                <EditOutlinedIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir ou inativar">
                              <IconButton color="error" onClick={() => deleteInput(input)} disabled={saving}>
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : null}
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}
          </Stack>
        )}

        <Dialog open={inputDialog.open} onClose={() => setInputDialog({ open: false, input: null })} maxWidth="sm" fullWidth>
          <DialogTitle>{inputDialog.input ? 'Editar item de estoque' : 'Adicionar item de estoque'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {inputDialogError ? <Alert severity="error">{inputDialogError}</Alert> : null}
              <TextField
                fullWidth
                label="Nome"
                value={inputForm.name}
                onChange={(event) => setInputForm((prev) => ({ ...prev, name: event.target.value }))}
              />
              <FormControl fullWidth>
                <InputLabel>Categoria</InputLabel>
                <Select
                  label="Categoria"
                  value={inputForm.categoryId}
                  onChange={(event) => setInputForm((prev) => ({ ...prev, categoryId: event.target.value }))}
                >
                  <MenuItem value="">Sem categoria</MenuItem>
                  {categories.filter((category) => category.active !== false).map((category) => (
                    <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Nova categoria"
                    value={quickCategoryName}
                    onChange={(event) => setQuickCategoryName(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        createQuickCategory();
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={createQuickCategory}
                    disabled={saving || !quickCategoryName.trim()}
                    sx={{ minWidth: { xs: '100%', sm: 210 }, whiteSpace: 'nowrap' }}
                  >
                    Criar e selecionar
                  </Button>
                </Stack>
              </Paper>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Unidade de compra</InputLabel>
                    <Select
                      label="Unidade de compra"
                      value={inputForm.purchaseUnit}
                      onChange={(event) => setInputForm((prev) => ({ ...prev, purchaseUnit: event.target.value, quantityPerPackage: '' }))}
                    >
                      <MenuItem value="Kg">Kg</MenuItem>
                      <MenuItem value="L">L</MenuItem>
                      <MenuItem value="Cx">Cx</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Unidade base" value={conversion.baseUnit} disabled />
                </Grid>
              </Grid>
              {inputForm.purchaseUnit === 'Cx' ? (
                <TextField
                  fullWidth
                  required
                  label="Unidades por caixa"
                  type="number"
                  value={inputForm.quantityPerPackage}
                  onChange={(event) => setInputForm((prev) => ({ ...prev, quantityPerPackage: event.target.value }))}
                />
              ) : null}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label={inputDialog.input ? 'Estoque atual' : 'Estoque inicial'}
                    type="number"
                    value={inputForm.currentStockBase}
                    disabled={Boolean(inputDialog.input)}
                    onChange={(event) => setInputForm((prev) => ({ ...prev, currentStockBase: event.target.value }))}
                    InputProps={{ endAdornment: <Chip size="small" label={conversion.baseUnit} /> }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Estoque mínimo"
                    type="number"
                    value={inputForm.minimumStockBase}
                    onChange={(event) => setInputForm((prev) => ({ ...prev, minimumStockBase: event.target.value }))}
                    InputProps={{ endAdornment: <Chip size="small" label={conversion.baseUnit} /> }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Custo da unidade de compra"
                    type="number"
                    value={inputForm.purchaseCost}
                    onChange={(event) => setInputForm((prev) => ({ ...prev, purchaseCost: event.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Custo convertido" value={formatCurrency(baseUnitCost)} disabled />
                </Grid>
              </Grid>
              <FormControlLabel
                control={<Switch checked={inputForm.active} onChange={(event) => setInputForm((prev) => ({ ...prev, active: event.target.checked }))} />}
                label="Ativo"
              />
              <Divider />
              <Typography variant="body2" color="text.secondary">
                Conversão: 1 {inputForm.purchaseUnit} = {conversion.conversionFactor || 0} {conversion.baseUnit}
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInputDialog({ open: false, input: null })}>Cancelar</Button>
            <Button variant="contained" onClick={saveInput} disabled={saving || !canManage || !inputForm.name.trim()}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={categoryDialog.open} onClose={() => setCategoryDialog({ open: false })} maxWidth="sm" fullWidth>
          <DialogTitle>Categorias de estoque</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {categoryDialogError ? <Alert severity="error">{categoryDialogError}</Alert> : null}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  fullWidth
                  label="Nova categoria"
                  value={newCategoryName}
                  onChange={(event) => setNewCategoryName(event.target.value)}
                />
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => saveCategory()} disabled={saving || !canManage || !newCategoryName.trim()}>
                  Criar
                </Button>
              </Stack>
              <Divider />
              {categories.length === 0 ? (
                <Typography color="text.secondary">Nenhuma categoria cadastrada.</Typography>
              ) : categories.map((category) => (
                <Paper key={category.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <TextField
                      fullWidth
                      label="Categoria"
                      value={categoryDrafts[category.id] || ''}
                      onChange={(event) => setCategoryDrafts((prev) => ({ ...prev, [category.id]: event.target.value }))}
                    />
                    <Chip color={category.active ? 'success' : 'default'} label={category.active ? 'Ativa' : 'Inativa'} />
                    <Button variant="outlined" onClick={() => saveCategory(category)} disabled={saving || !canManage}>
                      Salvar
                    </Button>
                    <IconButton color="error" onClick={() => deleteCategory(category)} disabled={saving || !canManage}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCategoryDialog({ open: false })}>Fechar</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={movementDialog.open} onClose={() => setMovementDialog({ open: false, input: null })} maxWidth="xs" fullWidth>
          <DialogTitle>Movimentar estoque</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {movementDialogError ? <Alert severity="error">{movementDialogError}</Alert> : null}
              <Typography sx={{ fontWeight: 800 }}>{movementDialog.input?.name}</Typography>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  label="Tipo"
                  value={movementForm.movementType}
                  onChange={(event) => setMovementForm((prev) => ({ ...prev, movementType: event.target.value }))}
                >
                  <MenuItem value="entrada">Entrada manual</MenuItem>
                  <MenuItem value="saida">Saída manual</MenuItem>
                  <MenuItem value="ajuste">Ajuste de inventário</MenuItem>
                  <MenuItem value="perda">Perda</MenuItem>
                </Select>
              </FormControl>
              {movementForm.movementType === 'ajuste' ? (
                <TextField
                  fullWidth
                  label="Novo saldo"
                  type="number"
                  value={movementForm.targetStockBase}
                  onChange={(event) => setMovementForm((prev) => ({ ...prev, targetStockBase: event.target.value }))}
                  InputProps={{ endAdornment: <Chip size="small" label={movementDialog.input?.baseUnit || ''} /> }}
                />
              ) : movementForm.movementType === 'entrada' ? (
                <TextField
                  fullWidth
                  label={`Quantidade comprada (${movementDialog.input?.purchaseUnit || ''})`}
                  type="number"
                  value={movementForm.purchaseQuantity}
                  onChange={(event) => setMovementForm((prev) => ({ ...prev, purchaseQuantity: event.target.value }))}
                  helperText={`Será registrada como ${formatNumber(toNumber(movementForm.purchaseQuantity) * Number(movementDialog.input?.conversionFactor || 0))} ${movementDialog.input?.baseUnit || ''}`}
                  InputProps={{ endAdornment: <Chip size="small" label={movementDialog.input?.purchaseUnit || ''} /> }}
                />
              ) : (
                <TextField
                  fullWidth
                  label="Quantidade"
                  type="number"
                  value={movementForm.quantityBase}
                  onChange={(event) => setMovementForm((prev) => ({ ...prev, quantityBase: event.target.value }))}
                  InputProps={{ endAdornment: <Chip size="small" label={movementDialog.input?.baseUnit || ''} /> }}
                />
              )}
              <TextField
                fullWidth
                label="Motivo"
                value={movementForm.reason}
                onChange={(event) => setMovementForm((prev) => ({ ...prev, reason: event.target.value }))}
                multiline
                rows={2}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMovementDialog({ open: false, input: null })}>Cancelar</Button>
            <Button variant="contained" onClick={saveMovement} disabled={saving || !canManage}>
              Registrar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={historyDialog.open} onClose={() => setHistoryDialog({ open: false, input: null })} maxWidth="md" fullWidth>
          <DialogTitle>Histórico de movimentações</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Typography sx={{ fontWeight: 800 }}>{historyDialog.input?.name}</Typography>
              {loadingMovements ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : movements.length === 0 ? (
                <Typography color="text.secondary">Nenhuma movimentação registrada.</Typography>
              ) : movements.map((movement) => (
                <Paper key={movement.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid size={{ xs: 12, md: 3 }}>
                      <Chip color={movement.movementType === 'entrada' || movement.movementType === 'estorno' ? 'success' : movement.movementType === 'ajuste' ? 'primary' : 'warning'} label={MOVEMENT_LABELS[movement.movementType] || movement.movementType} />
                    </Grid>
                    <Grid size={{ xs: 6, md: 2 }}>
                      <Typography variant="body2" color="text.secondary">Quantidade</Typography>
                      <Typography sx={{ fontWeight: 800 }}>{formatNumber(movement.quantityBase)} {historyDialog.input?.baseUnit}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, md: 2 }}>
                      <Typography variant="body2" color="text.secondary">Saldo</Typography>
                      <Typography sx={{ fontWeight: 800 }}>{formatNumber(movement.previousStockBase)} -&gt; {formatNumber(movement.newStockBase)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                      <Typography variant="body2" color="text.secondary">Motivo</Typography>
                      <Typography>{movement.reason || '-'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(movement.createdAt).toLocaleString('pt-BR')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHistoryDialog({ open: false, input: null })}>Fechar</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={issueDialogOpen} onClose={() => setIssueDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Alertas de baixa automática</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {issues.length === 0 ? (
                <Typography color="text.secondary">Nenhum alerta registrado.</Typography>
              ) : issues.map((issue) => (
                <Paper key={issue.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack spacing={1}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                      <Box>
                        <Typography sx={{ fontWeight: 800 }}>{issue.message}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {issue.relatedOrderSource || '-'} • Pedido {issue.relatedOrderId || '-'} • {new Date(issue.createdAt).toLocaleString('pt-BR')}
                        </Typography>
                      </Box>
                      <Chip color={issue.resolved ? 'success' : 'warning'} label={issue.resolved ? 'Resolvido' : 'Pendente'} />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {JSON.stringify(issue.details || {}, null, 2)}
                    </Typography>
                    {!issue.resolved && canManage ? (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button size="small" variant="outlined" startIcon={<ReplayOutlinedIcon />} onClick={() => reprocessIssue(issue)} disabled={saving}>
                          Reprocessar baixa
                        </Button>
                        <Button size="small" variant="outlined" color="success" startIcon={<CheckCircleOutlineIcon />} onClick={() => resolveIssue(issue)} disabled={saving}>
                          Resolver manualmente
                        </Button>
                      </Stack>
                    ) : null}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIssueDialogOpen(false)}>Fechar</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={cmvDialogOpen} onClose={() => setCmvDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Relatório de CMV</DialogTitle>
          <DialogContent>
            {loadingCmv ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Produtos</Typography>
                {(cmvReport.products || []).length === 0 ? (
                  <Typography color="text.secondary">Nenhum produto com ficha técnica para exibir.</Typography>
                ) : (cmvReport.products || []).map((product) => (
                  <Paper key={product.productId} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Grid container spacing={1.5} alignItems="center">
                      <Grid size={{ xs: 12, md: 5 }}>
                        <Typography sx={{ fontWeight: 800 }}>{product.productName}</Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 2 }}>
                        <Typography variant="body2" color="text.secondary">Preço</Typography>
                        <Typography sx={{ fontWeight: 800 }}>{formatCurrency(product.price)}</Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 2 }}>
                        <Typography variant="body2" color="text.secondary">Ficha</Typography>
                        <Typography sx={{ fontWeight: 800 }}>{formatCurrency(product.sheetCost)}</Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Chip color={(product.cmvPercent || 0) > 40 ? 'warning' : 'success'} label={product.cmvPercent === null ? 'Sem preço' : `${formatNumber(product.cmvPercent)}% CMV`} />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
                <Divider />
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Pedidos recentes</Typography>
                {(cmvReport.orders || []).length === 0 ? (
                  <Typography color="text.secondary">Nenhuma baixa por venda registrada.</Typography>
                ) : (cmvReport.orders || []).map((order) => (
                  <Paper key={`${order.relatedOrderSource}-${order.relatedOrderId}`} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Grid container spacing={1.5} alignItems="center">
                      <Grid size={{ xs: 12, md: 5 }}>
                        <Typography sx={{ fontWeight: 800 }}>{order.relatedOrderSource} • {order.relatedOrderId}</Typography>
                        <Typography variant="body2" color="text.secondary">{new Date(order.lastMovementAt).toLocaleString('pt-BR')}</Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">CMV estimado</Typography>
                        <Typography sx={{ fontWeight: 800 }}>{formatCurrency(order.estimatedCmv)}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCmvDialogOpen(false)}>Fechar</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={toast.open}
          autoHideDuration={3500}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert severity={toast.severity} onClose={() => setToast((prev) => ({ ...prev, open: false }))}>
            {toast.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default Estoque;
